/**
 * Confettis de victoire (spec 05 §5.4) : canvas overlay plein écran (z toast),
 * particules GSAP aux couleurs panda/bambou/raisin/soleil, auto-nettoyage.
 * Reduced-motion → rien (le feedback de victoire passe par l'UI, pas l'effet).
 * GSAP possède l'overlay (jamais les transforms des composants → Framer).
 */
import { gsap } from 'gsap';
import { useCallback, useEffect, useRef } from 'react';
import { prefersReducedMotion } from '../motion/useReducedMotion';
import { colorTokens, readTokenRgb, zIndex } from '../tokens';

export type ConfettiOptions = {
  /** Nombre de particules (défaut 140). */
  count?: number;
  /** Origine de l'explosion, en fraction de l'écran 0..1 (défaut centre haut). */
  origin?: { x: number; y: number };
  /** Durée totale en secondes (défaut 2.6). */
  durationSec?: number;
};

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  size: number;
  color: string;
  shape: 'rect' | 'circle';
};

export type ConfettiCleanup = () => void;

const GRAVITY = 1100; // px/s²
const DRAG = 0.16;

/**
 * Tire une salve de confettis. Retourne une fonction de nettoyage (facultative :
 * l'effet se nettoie tout seul à la fin de la timeline).
 */
export function fireConfetti(options: ConfettiOptions = {}): ConfettiCleanup {
  if (typeof document === 'undefined' || prefersReducedMotion()) return () => {};

  const { count = 140, origin = { x: 0.5, y: 0.4 }, durationSec = 2.6 } = options;

  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  canvas.setAttribute('data-confetti', 'true');
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: String(zIndex.toast),
  });
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  document.body.appendChild(canvas);

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.floor(window.innerWidth * dpr);
  canvas.height = Math.floor(window.innerHeight * dpr);
  ctx.scale(dpr, dpr);

  const palette = [
    readTokenRgb(colorTokens.panda500, '255 90 31'),
    readTokenRgb(colorTokens.bamboo500, '34 197 94'),
    readTokenRgb(colorTokens.grape500, '139 92 246'),
    readTokenRgb(colorTokens.sun400, '250 204 21'),
  ];

  const ox = origin.x * window.innerWidth;
  const oy = origin.y * window.innerHeight;
  const particles: Particle[] = Array.from({ length: count }, (_, i) => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
    const speed = 420 + Math.random() * 620;
    return {
      x: ox,
      y: oy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      rot: Math.random() * Math.PI * 2,
      vrot: (Math.random() - 0.5) * 12,
      size: 5 + Math.random() * 7,
      color: palette[i % palette.length] as string,
      shape: Math.random() < 0.65 ? 'rect' : 'circle',
    };
  });

  let cleaned = false;
  const render = () => {
    const dt = gsap.ticker.deltaRatio(60) / 60;
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    for (const p of particles) {
      p.vy += GRAVITY * dt;
      p.vx *= 1 - DRAG * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vrot * dt;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === 'rect') {
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2.4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  };

  const cleanup: ConfettiCleanup = () => {
    if (cleaned) return;
    cleaned = true;
    gsap.ticker.remove(render);
    timeline.kill();
    canvas.remove();
  };

  gsap.ticker.add(render);
  const timeline = gsap.timeline({ onComplete: cleanup });
  timeline.to(canvas, { opacity: 0, duration: 0.5, ease: 'power1.in' }, durationSec - 0.5);

  return cleanup;
}

/**
 * Hook confettis : `const { fire } = useConfetti()` puis `fire()` à la victoire.
 * Nettoie l'effet en cours au démontage du composant.
 */
export function useConfetti() {
  const cleanupRef = useRef<ConfettiCleanup | null>(null);

  useEffect(
    () => () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    },
    [],
  );

  const fire = useCallback((options?: ConfettiOptions) => {
    cleanupRef.current?.();
    cleanupRef.current = fireConfetti(options);
  }, []);

  return { fire };
}
